import useGetMyAvailableTags from '@/api/queries/me/useGetMyAvailableTags';
import {
  GetPipelineStageProjectsInput,
  PipelineStageProjectSearchTermTarget,
} from '@/gql/graphql';
import useIsContainerBeyondBreakpoint from '@/hooks/useIsContainerBeyondBreakpoint';
import {
  Badge,
  Button,
  InputGroup,
  SelectSizeVariant,
  UntitledLoading,
} from '@payaca/ambrosia';

import Tag from '@payaca/components/tag/Tag';
import TagSelectCombobox from '@payaca/components/tagSelectCombobox/TagSelectCombobox';
import UntitledIcon from '@payaca/untitled-icons';
import { motion } from 'motion/react';
import { ReactNode, useMemo, useState } from 'react';
import PipelineSortControl from './PipelineSortControl';

const SEARCH_TARGET_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Search all' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'PROJECT', label: 'Project' },
  { value: 'PROJECT_ADDRESS', label: 'Site address' },
  { value: 'PROPOSAL', label: 'Proposal' },
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'CHANGE_PROPOSAL', label: 'Change proposal' },
  { value: 'TAG', label: 'Tag' },
];

type Props = {
  isLoading: boolean;
  input?: GetPipelineStageProjectsInput;
  onChangeInput?: React.Dispatch<
    React.SetStateAction<GetPipelineStageProjectsInput | undefined>
  >;
  extraControls?: ReactNode;
};

function PipelineStageFiltering({
  isLoading,
  input,
  onChangeInput,
  extraControls,
}: Props) {
  const { data: availableTags } = useGetMyAvailableTags();
  const [localSearchTerm, setLocalSearchTerm] = useState(
    input?.searchTerm ?? ''
  );
  const [localSearchTermTarget, setLocalSearchTermTarget] = useState<
    PipelineStageProjectSearchTermTarget | ''
  >(input?.searchTermTarget?.[0] ?? '');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const isMdAndUp = useIsContainerBeyondBreakpoint('md');

  const mappedTags = useMemo(() => {
    if (!availableTags) return [];
    return availableTags
      .filter((tag): tag is typeof tag & { publicId: string } =>
        Boolean(tag.publicId)
      )
      .map((tag) => ({
        id: tag.publicId,
        tagText: tag.name,
        tagColour: tag.colour ?? '',
      }));
  }, [availableTags]);

  const selectedTagIds = input?.tagIds ?? [];

  const handleTagsChange = (tagIds: string[]) => {
    onChangeInput?.({
      ...input,
      tagIds: tagIds.length > 0 ? tagIds : undefined,
    });
  };

  const handleSearchTargetChange = (value: string) => {
    const target = value as PipelineStageProjectSearchTermTarget | '';
    setLocalSearchTermTarget(target);

    // Trigger a request only if a search term is already set; changing the
    // target alone shouldn't fire an empty search.
    if (input?.searchTerm) {
      onChangeInput?.({
        ...input,
        searchTermTarget: target ? [target] : undefined,
      });
    }
  };

  const handleSearch = () => {
    onChangeInput?.({
      ...input,
      searchTerm: localSearchTerm || undefined,
      searchTermTarget: localSearchTermTarget
        ? [localSearchTermTarget]
        : undefined,
    });
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const selectedSearchTarget = localSearchTermTarget;

  const renderTargetSelect = (className: string) => (
    <InputGroup.Select
      placeholder="Search all"
      options={SEARCH_TARGET_OPTIONS}
      value={selectedSearchTarget}
      onChange={(value) =>
        handleSearchTargetChange(typeof value === 'string' ? value : '')
      }
      className={className}
    />
  );

  const sortControl = (
    <PipelineSortControl input={input} onChangeInput={onChangeInput} />
  );

  const tagFilter = (
    <TagSelectCombobox
      multiple
      placeholder="Tags"
      value={selectedTagIds}
      onChange={handleTagsChange}
      availableTags={mappedTags}
      className={isMdAndUp ? 'w-40' : 'w-full'}
      sizeVariant={SelectSizeVariant.SM}
      CustomSelected={({ selectedOptions }) => {
        if (selectedOptions.length === 0) return null;
        if (selectedOptions.length > 1) {
          return <span>Tags - {selectedOptions.length} selected</span>;
        }
        return (
          <span className="flex">
            <Tag colour={selectedOptions?.[0]?.metadata?.tagColour}>
              {selectedOptions?.[0]?.metadata?.tagText || ''}
            </Tag>
          </span>
        );
      }}
    />
  );

  const loadingIndicator = isLoading && (
    <motion.div
      className="inline-flex"
      transition={{ delay: 0.3 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <UntitledLoading />
    </motion.div>
  );

  if (isMdAndUp) {
    return (
      <div className="flex justify-between items-center gap-4 w-full">
        <div className="flex items-center gap-2.5 flex-nowrap">
          {sortControl}
          <InputGroup sizeVariant="sm" className="w-full">
            {renderTargetSelect('w-32')}
            <InputGroup.Input
              className="w-52"
              placeholder="Search"
              value={localSearchTerm}
              onChange={(value) => setLocalSearchTerm(value)}
              onKeyDown={handleSearchKeyDown}
            />
            {!!localSearchTerm && (
              <InputGroup.Button
                variant="secondary"
                onClick={() => {
                  setLocalSearchTerm('');
                  setLocalSearchTermTarget('');
                  onChangeInput?.({
                    ...input,
                    searchTerm: undefined,
                    searchTermTarget: undefined,
                  });
                }}
              >
                <UntitledIcon className="h-3 w-3" name="x-close" />
              </InputGroup.Button>
            )}
            <InputGroup.Button variant="secondary" onClick={handleSearch}>
              <UntitledIcon name="search-lg.3" className="size-4" />
            </InputGroup.Button>
          </InputGroup>
          {tagFilter}
          {loadingIndicator}
        </div>
        {extraControls && (
          <div className="flex items-center gap-4">{extraControls}</div>
        )}
      </div>
    );
  }

  const hasAssigneeFilter =
    (input?.assignedTo?.filter(Boolean).length ?? 0) > 0;
  const mobileFilterCount =
    (selectedTagIds.length > 0 ? 1 : 0) +
    (selectedSearchTarget ? 1 : 0) +
    (hasAssigneeFilter ? 1 : 0);

  return (
    <div className="flex flex-col gap-2.5 w-full">
      <div className="flex items-center gap-2">
        <InputGroup sizeVariant="sm" className="flex-1">
          <InputGroup.Input
            className="flex-1"
            placeholder="Search"
            value={localSearchTerm}
            onChange={(value) => setLocalSearchTerm(value)}
            onKeyDown={handleSearchKeyDown}
          />
          {!!localSearchTerm && (
            <InputGroup.Button
              variant="secondary"
              onClick={() => {
                setLocalSearchTerm('');
                setLocalSearchTermTarget('');
                onChangeInput?.({
                  ...input,
                  searchTerm: undefined,
                  searchTermTarget: undefined,
                });
              }}
            >
              <UntitledIcon className="h-3 w-3" name="x-close" />
            </InputGroup.Button>
          )}
          <InputGroup.Button variant="secondary" onClick={handleSearch}>
            <UntitledIcon name="search-lg.3" className="size-4" />
          </InputGroup.Button>
        </InputGroup>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setMobileFiltersOpen((open) => !open)}
          aria-expanded={mobileFiltersOpen}
          aria-controls="pipeline-mobile-filters"
        >
          <UntitledIcon name="filter-lines.3" className="h-3.5 w-3.5" />
          Filters
          {mobileFilterCount > 0 && (
            <Badge variant="solid" colour="black" size="sm" rounded>
              {mobileFilterCount}
            </Badge>
          )}
        </Button>
        {loadingIndicator}
      </div>
      {mobileFiltersOpen && (
        <div
          id="pipeline-mobile-filters"
          className="flex flex-col gap-2.5 pt-1"
        >
          {sortControl}
          <InputGroup sizeVariant="sm" className="w-full">
            {renderTargetSelect('w-full')}
          </InputGroup>
          {tagFilter}
          {extraControls && (
            <div className="flex items-center gap-4 flex-wrap">
              {extraControls}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PipelineStageFiltering;
